import { cn } from '@src/lib/utils';

import useLangText from '@shared/hooks/useLangText';
import {
    ProcessFieldBase,
    type ProcessFieldText as ProcessFieldTextType,
} from '@shared/types/process';

import { HtmlEditorField } from '../../HtmlEditorField';

import { ShadcnForm } from '@shadcn-features/core';
import { ShadcnFormTextAreaProps } from '@shadcn-features/core/ShadcnForm/fields/ShadcnFormTextArea';

interface Props extends ShadcnFormTextAreaProps {
    field: ProcessFieldBase & ProcessFieldTextType;
}

export function ProcessFieldTextField({ field, fieldProps, ...props }: Props) {
    const getLangText = useLangText();

    if (props.readOnly) {
        return (
            <ShadcnForm.Item
                {...props}
                defaultValue={props.initialValue}
                tooltip={getLangText(field?.tooltip ?? '')}
            >
                {({ value }) => {
                    const text = typeof value === 'string' ? value : '';
                    const isEmpty = !(
                        field.options.editor
                            ? new DOMParser().parseFromString(text, 'text/html')
                                  .body.textContent
                            : text
                    )?.trim();

                    return (
                        <div
                            className={cn(
                                'text-sm leading-[normal] font-semibold wrap-anywhere whitespace-pre-wrap text-(--color-card-text)',
                                !isEmpty &&
                                    'max-h-[121px] min-h-10 overflow-hidden rounded-xl bg-(--color-body) p-3',
                            )}
                        >
                            {isEmpty ? (
                                '-'
                            ) : field.options.editor ? (
                                <HtmlEditorField
                                    value={text}
                                    isShowOnlyHtml
                                    className='[&_.ql-container]:min-h-0! [&_.ql-container]:rounded-none! [&_.ql-editor]:line-clamp-6 [&_.ql-editor]:min-h-0! [&_.ql-editor]:overflow-hidden! [&_.ql-editor]:text-sm! [&_.ql-editor]:leading-4! [&_.ql-editor]:font-semibold! [&_.quill]:rounded-none!'
                                />
                            ) : (
                                <p className='line-clamp-6 leading-4'>{text}</p>
                            )}
                        </div>
                    );
                }}
            </ShadcnForm.Item>
        );
    }

    if (field.options.editor) {
        return (
            <ShadcnForm.Item {...props}>
                <HtmlEditorField isShowOnlyHtml={props.readOnly} />
            </ShadcnForm.Item>
        );
    }

    return (
        <ShadcnForm.TextArea
            fieldProps={fieldProps}
            rows={field.options.rows ?? undefined}
            {...props}
            tooltip={getLangText(field?.tooltip ?? '')}
        ></ShadcnForm.TextArea>
    );
}
